import { gql } from '@apollo/client'

// Fragments

export const PROJECT_PARTS_FRAGMENT = gql`
  fragment ProjectParts on Project {
    meta
    screens
    branding
    integrations
    profileViewer
    products
    triggers
    modules
    userContext
    dataElements
    rules
  }
`
export const PROJECT_DB_PROPS_FRAGMENT = gql`
  fragment ProjectDbProps on Project {
    id
    alias
    metadata {
      alias
      title
      description
      image
      expiresAt
    }
    owner
    createdAt
    updatedAt
    updatedBy
    archivedAt
    editors
    observers
    isPublic
    isFeatured
    hasPassword
  }
`

export const UPDATED_PROJECT_FRAGMENT = gql`
  fragment UpdatedProject on Project {
    id
    alias
    metadata {
      alias
      title
      description
      image
      expiresAt
    }
    owner
    createdAt
    updatedAt
    updatedBy
    archivedAt
    editors
    observers
    isPublic
    isFeatured
    ...ProjectParts
  }
`

export const NEW_PROJECT_FRAGMENT = gql`
  fragment NewProject on Projects {
    id
    alias
    metadata {
      alias
      title
      description
      image
      expiresAt
    }
    owner
    createdAt
    updatedAt
    updatedBy
    archivedAt
    editors
    observers
    isPublic
    isFeatured
    meta
  }
`

// Queries

export const INITIAL_DATA_QUERY = gql`
  query initialData($environment: String) {
    features(scope: "argon", environment: $environment) {
      name
    }
    me {
      id
      email
      isSuperUser
    }
  }
`

export const MY_PROJECTS_QUERY = gql`
  query myProjects {
    myProjects {
      id
      alias
      owner
      createdAt
      updatedAt
      updatedBy
      archivedAt
      editors
      observers
      meta
      integrations
    }
  }
`

export const PROJECT_QUERY = gql`
  ${PROJECT_DB_PROPS_FRAGMENT}
  ${PROJECT_PARTS_FRAGMENT}
  query project($id: String!) {
    project(id: $id) {
      ...ProjectDbProps
      ...ProjectParts
      workspace {
        livedemosWorkspaceId
      }
    }
  }
`

export const MY_ACC_QUERY = gql`
  {
    myACC {
      id
      url
      tenantID
      imsOrg {
        id
        name
      }
    }
  }
`

export const MY_ENVIRONMENTS_QUERY = gql`
  query myEnvironments {
    myEnvironments {
      imsOrgs {
        id
        name
        targetClientCode
        tenantID
      }
      sandboxes {
        id
        sandboxName
        imsOrgID
      }
      launchProperties {
        id
        appID
        propertyID
        propertyName
        primarySolution
        type
        createdAt
        imsOrgID
        aepID
      }
    }
  }
`

export const MY_LAUNCH_QUERY = gql`
  query myLaunch {
    myLaunch {
      id
      propertyName
      type
      appID
      launchUrl
      createdAt
      primarySolution
    }
  }
`

export const MY_IMS_ORGS = gql`
  query myIMSOrgs {
    myIMSOrgs {
      id
      name
      targetClientCode
      tenantID
    }
  }
`

// Mutations

export const CREATE_PROJECT_MUTATION = gql`
  ${PROJECT_DB_PROPS_FRAGMENT}
  mutation createProject($templateId: String!, $title: String, $thumbnail: String, $environment: String) {
    createProject(templateId: $templateId, title: $title, thumbnail: $thumbnail, environment: $environment) {
      ...ProjectDbProps
    }
  }
`

export const UPDATE_PROJECT_MUTATION = gql`
  ${PROJECT_DB_PROPS_FRAGMENT}
  mutation updateProject($id: String!, $content: JSON!) {
    updateProject(id: $id, content: $content) {
      ...ProjectDbProps
    }
  }
`

export const UPDATE_PROJECT_PART_MUTATION = gql`
  mutation updateProjectPart($id: String, $partName: String, $partContent: JSON) {
    updateProjectPart(id: $id, partName: $partName, partContent: $partContent)
  }
`

export const UPDATE_PROJECT_SCREEN_MUTATION = gql`
  mutation updateProjectScreen($id: String, $screenName: String, $payload: JSON) {
    updateProjectScreen(id: $id, screenName: $screenName, payload: $payload)
  }
`

export const RENAME_PROJECT_SCREEN_MUTATION = gql`
  mutation renameProjectScreen($id: String, $oldScreenName: String!, $newScreenName: String!, $newScreenTitle: String) {
    renameProjectScreen(
      id: $id
      oldScreenName: $oldScreenName
      newScreenName: $newScreenName
      newScreenTitle: $newScreenTitle
    )
  }
`

export const ARCHIVE_PROJECT_MUTATION = gql`
  mutation archiveProject($id: String!) {
    archiveProject(id: $id)
  }
`
export const DELETE_LAUNCH_MUTATION = gql`
  mutation deleteLaunch($id: Int!) {
    deleteLaunch(id: $id)
  }
`

export const DELETE_DATASTREAM_MUTATION = gql`
  mutation deleteDataStream($propertyID: Int!) {
    deleteDataStream(propertyID: $propertyID)
  }
`

export const DUPLICATE_PROJECT_MUTATION = gql`
  ${PROJECT_DB_PROPS_FRAGMENT}
  ${PROJECT_PARTS_FRAGMENT}
  mutation duplicateProject($id: String!, $title: String) {
    duplicateProject(id: $id, title: $title) {
      ...ProjectDbProps
      ...ProjectParts
    }
  }
`

export const TRANSFER_PROJECT_OWNERSHIP = gql`
  ${PROJECT_DB_PROPS_FRAGMENT}
  ${PROJECT_PARTS_FRAGMENT}
  mutation transferProjectOwnership($id: String!, $newOwner: String!) {
    transferProjectOwnership(id: $id, newOwner: $newOwner) {
      ...ProjectDbProps
      ...ProjectParts
    }
  }
`

export const UPDATE_PERMISSION = gql`
  ${PROJECT_DB_PROPS_FRAGMENT}
  ${PROJECT_PARTS_FRAGMENT}
  mutation updateProjectPermission($id: String!, $targetUser: String!, $permission: String) {
    updateProjectPermission(id: $id, targetUser: $targetUser, permission: $permission) {
      ...ProjectDbProps
      ...ProjectParts
    }
  }
`

export const UPDATE_PASSWORD = gql`
  mutation setProjectPassword($id: String!, $password: String) {
    setProjectPassword(id: $id, password: $password)
  }
`

export const UPDATE_METADATA = gql`
  mutation upsertProjectMetadata($id: String!, $metadata: ProjectMetadataInput) {
    upsertProjectMetadata(id: $id, metadata: $metadata) {
      alias
      title
      description
      image
      expiresAt
    }
  }
`

export const DELETE_METADATA = gql`
  mutation deleteProjectMetadata($id: String!) {
    deleteProjectMetadata(id: $id)
  }
`

export const UPDATE_PUBLIC_STATUS = gql`
  ${PROJECT_DB_PROPS_FRAGMENT}
  ${PROJECT_PARTS_FRAGMENT}
  mutation updateProjectPublicStatus($id: String!, $status: Boolean!) {
    updateProjectPublicStatus(id: $id, status: $status) {
      ...ProjectDbProps
      ...ProjectParts
    }
  }
`

export const GET_PROJECT_TEMPLATES = gql`
  query projectTemplates($environment: String) {
    projectTemplates(environment: $environment) {
      id
      title
      description
      image
      type
      order
    }
  }
`

export const COLLECT_OPPORTUNITIES = gql`
  mutation collectOpportunities($opportunities: [OpportunityInput]) {
    collectOpportunities(opportunities: $opportunities) {
      drNumber
      projectID
      workspaceID
      createdBy
    }
  }
`

// Subscriptions

export const PROJECT_PART_UPDATED_SUBSCRIPTION = gql`
  subscription OnProjectPartUpdated($projectId: String!, $partName: String) {
    projectPartUpdated(projectId: $projectId, partName: $partName)
  }
`

export const PROJECT_SCREEN_UPDATED_SUBSCRIPTION = gql`
  subscription OnProjectScreenUpdated($projectId: String!) {
    projectScreenUpdated(projectId: $projectId)
  }
`
